import { round } from "lodash";

import { DesignToken } from "@livechat/design-system-react-components";

import { ReportDistribution } from "constants/reports/distribution";
import { duration } from "helpers/date";
import { ITicketResolutionTime, ITicketResolutionTimePerEntity } from "services/api/report/interfaces";
import { withVar } from "themes/helpers";

import { getLabelsFromPeriod, getSeriesDataFromPeriod, sumValueFromPeriod } from "../helpers";
import { IChartData } from "../interfaces";

const creationColor = withVar(DesignToken.DecorBlue500);
const assignmentColor = withVar(DesignToken.DecorGreen500);

interface ITicketResolutionTimeData {
  data: {
    ticketResolutionTime: {
      sumSecondsFromCreation: number;
      sumSecondsFromAssignment: number;
      countFromCreation: number;
      countFromAssignment: number;
      count: number;
    };
    ticketResolutionTimePerTeam: ITicketResolutionTimePerEntity;
    ticketResolutionTimePerAgent: ITicketResolutionTimePerEntity;
  };
}

interface IResolutionTimeData {
  resolutionTime: IChartData;
  resolutionTimePerAgent: ITicketResolutionTimePerEntity[];
  resolutionTimePerTeam: ITicketResolutionTimePerEntity[];
}

function mapToArray(data: ITicketResolutionTime): ITicketResolutionTimePerEntity[] {
  return Object.entries(data).map(([id, values]) => ({
    id,
    ...values,
  }));
}

export function ticketResolutionTimeDeserializer(
  { data }: ITicketResolutionTimeData,
  distribution: ReportDistribution
): IResolutionTimeData {
  const { ticketResolutionTime, ticketResolutionTimePerTeam, ticketResolutionTimePerAgent } = data;
  const creationDataSeconds = getSeriesDataFromPeriod(ticketResolutionTime, "sumSecondsFromCreation", distribution);
  const assignmentDataSeconds = getSeriesDataFromPeriod(ticketResolutionTime, "sumSecondsFromAssignment", distribution);
  const ticketCountData = getSeriesDataFromPeriod(ticketResolutionTime, "count", distribution);
  const ticketCountFromCreationData = getSeriesDataFromPeriod(ticketResolutionTime, "countFromCreation", distribution);
  const ticketCountFromAssignmentData = getSeriesDataFromPeriod(
    ticketResolutionTime,
    "countFromAssignment",
    distribution
  );

  const averageCreationDataSeconds = creationDataSeconds.map(
    (_, index) => creationDataSeconds[index] / (ticketCountFromCreationData[index] || 1)
  );

  const averageAssignmentDataSeconds = assignmentDataSeconds.map(
    (_, index) => assignmentDataSeconds[index] / (ticketCountFromAssignmentData[index] || 1)
  );

  const creationTotal = sumValueFromPeriod(ticketResolutionTime, "sumSecondsFromCreation");
  const assignmentTotal = sumValueFromPeriod(ticketResolutionTime, "sumSecondsFromAssignment");

  const ticketCountFromCreationTotal = sumValueFromPeriod(ticketResolutionTime, "countFromCreation");
  const ticketCountFromAssignmentTotal = sumValueFromPeriod(ticketResolutionTime, "countFromAssignment");

  const creationAverage = creationTotal / (ticketCountFromCreationTotal || 1);
  const assignmentAverage = assignmentTotal / (ticketCountFromAssignmentTotal || 1);

  const labels = getLabelsFromPeriod(ticketResolutionTime, distribution);

  const calculatePercentage = (value: number, index: number): number =>
    round((value / ticketCountData[index] || 1) * 100);

  const total = {
    id: "total",
    total: creationTotal + assignmentTotal,
    averageSecondsFromCreation: creationAverage,
    averageSecondsFromAssignment: assignmentAverage,
    countFromCreation: ticketCountFromCreationTotal,
    countFromAssignment: ticketCountFromAssignmentTotal,
    count: ticketCountFromCreationTotal + ticketCountFromAssignmentTotal,
  };

  return {
    resolutionTimePerAgent: ticketResolutionTimePerAgent ? [total, ...mapToArray(ticketResolutionTimePerAgent)] : [],
    resolutionTimePerTeam: ticketResolutionTimePerTeam ? [total, ...mapToArray(ticketResolutionTimePerTeam)] : [],
    resolutionTime: {
      summary: [
        {
          name: "Avg. time from ticket creation",
          value: duration(creationAverage),
          color: creationColor,
          rawValue: creationAverage,
        },
        {
          name: "Avg. time from assignment to agent",
          value: duration(assignmentAverage),
          color: assignmentColor,
          rawValue: assignmentAverage,
        },
      ],
      labels,
      series: [
        {
          name: "Avg. time from ticket creation",
          data: averageCreationDataSeconds,
          color: creationColor,
          extras: {
            count: ticketCountFromCreationData,
            percentage: ticketCountFromCreationData.map(calculatePercentage),
            customAggregate: creationAverage,
          },
        },
        {
          name: "Avg. time from assignment to agent",
          data: averageAssignmentDataSeconds,
          color: assignmentColor,
          extras: {
            count: ticketCountFromAssignmentData,
            percentage: ticketCountFromAssignmentData.map(calculatePercentage),
            customAggregate: assignmentAverage,
          },
        },
      ],
    },
  };
}
